import { statusOptions } from '@/common/options';
import {
  PrimaryButton,
  PrimaryOutlineButton,
} from '@/components/button/button-component';
import Input, {
  RawInput,
} from '@/components/react-hook-form-inputs/input/input-component';
import RadioButtonGroup, {
  RawRadioButtonGroup,
} from '@/components/react-hook-form-inputs/radio-button/radio-button-component';
import SelectSearch, {
  RawSelect,
} from '@/components/react-hook-form-inputs/select-search/select-search-component';
import Tooltip from '@/components/tooltip/tooltip-component';
import { useQuery } from '@/hooks/use-api';
import {
  requiredValidationDropdownMessage,
  requiredValidationMessage,
} from '@/utils/validations';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { BsInfoCircleFill, BsX } from 'react-icons/bs';

import getCategoriesSelectApi from '@/api/get/get-categories-select';
import UploadImageThumbnail from '@/components/upload-image-thumbnail/upload-image-thumbnail';
import Styled from './category-add-edit-form-style';

const propTypes = {
  setModalAction: PropTypes.func,
  modalAction: PropTypes.shape({
    id: PropTypes.string,
  }),
  loading: PropTypes.bool,
  methods: PropTypes.shape({
    formState: PropTypes.object,
    register: PropTypes.func,
    control: PropTypes.func,
    watch: PropTypes.func,
  }).isRequired,
  isRaw: PropTypes.bool,
  onSubmit: PropTypes.func,
};

const defaultProps = {
  setModalAction: () => {},
  modalAction: {},
  loading: false,
  isRaw: false,
  onSubmit: undefined,
};

/**
 * Page that shows the form to add or edit a Category.
 * @param {object} props The props object.
 * @param {Function} props.setModalAction The function to set the modal action.
 * @param {object} props.modalAction The modal action object.
 * @param {boolean} props.loading The loading state.
 * @param {object} props.methods The form methods.
 * @param {boolean} props.isRaw The raw state.
 * @param {Function} props.onSubmit The on submit function.
 * @returns {JSX.Element} The Category Add Edit Form component.
 */
const CategoryAddEditFormRaw = ({
  setModalAction,
  modalAction,
  loading,
  methods,
  isRaw,
  onSubmit,
}) => {
  const [categoryStatus, setCategoryStatus] = useState('');

  const { formState, register, control, watch } = methods;

  const formLoading = formState?.isLoading;

  const closeCreateEditModal = () => {
    setModalAction({ label: '', id: null });
  };

  const categoryStatusInitialColor = watch('status');

  const radioContainerStyle = { width: '100%' };
  const mainContainerStyle = { width: '80%', marginLeft: 5 };

  const multiSelectOptions = [
    { value: 'is_single_select', label: 'Single Selection' },
    { value: 'is_multi_select', label: 'Multiple Selection' },
  ];

  const InputField = isRaw ? RawInput : Input;
  const SelectField = isRaw ? RawSelect : SelectSearch;
  const RadioInput = isRaw ? RawRadioButtonGroup : RadioButtonGroup;

  const { data, loading: loadingAllCategories } = useQuery(
    getCategoriesSelectApi,
  );

  const generateCategoryNames = (category) => {
    const parentCategory = data?.find(
      (categoryItem) => categoryItem.id === category?.parent_category,
    );

    if (!parentCategory) {
      return category.name;
    }

    const ancestorCategoryName = generateCategoryNames(parentCategory);

    return `${category.name} - ${ancestorCategoryName}`;
  };

  const parentCategories = data?.map((cat) => ({
    value: cat.id,
    label: generateCategoryNames(cat),
  }));

  const isLoading = loadingAllCategories || formLoading || loading;

  return (
    <Styled.Main>
      <Styled.FormContainer>
        <InputField
          disabled={isLoading}
          label="Category Name"
          placeholder="Name"
          name="name"
          registerOptions={{
            required: requiredValidationMessage,
          }}
          formState={formState}
          register={register}
        />
        <InputField
          disabled={isLoading}
          label="Category Description"
          placeholder="Description"
          name="description"
          registerOptions={{
            required: requiredValidationMessage,
          }}
          formState={formState}
          register={register}
        />
        <InputField
          disabled={isLoading}
          label="Category Slug"
          placeholder="Slug"
          name="slug"
          registerOptions={{
            required: requiredValidationMessage,
          }}
          formState={formState}
          register={register}
        />
        <UploadImageThumbnail name="icon" loading={isLoading} />
        <SelectField
          cy="selCategoryStatus"
          header="Parent Category"
          name="parent_category"
          placeholder=""
          options={parentCategories}
          isLoading={isLoading}
          isDisabled={isLoading}
          customStyle={{ marginBottom: 30 }}
          maxMenuHeight={200}
          control={control}
          formState={formState}
        />
        <SelectField
          cy="selCategoryStatus"
          header="Status"
          name="status"
          label="Please select a status"
          options={statusOptions}
          isLoading={isLoading}
          isDisabled={isLoading}
          icon={
            <Styled.StatusCircle
              status={categoryStatusInitialColor || categoryStatus}
            />
          }
          required={{
            required: requiredValidationDropdownMessage,
            onChange: (e) => {
              setCategoryStatus(e.target.value);
            },
          }}
          control={control}
          formState={formState}
        />
        <Styled.RadioButtonGroup>
          <Styled.RadioButtonContainer>
            <RadioInput
              radioContainerStyle={radioContainerStyle}
              mainContainerStyle={mainContainerStyle}
              options={multiSelectOptions}
              disabled={isLoading}
              name="is_multi_select"
              registerOptions={{
                required: requiredValidationMessage,
              }}
              formState={formState}
              control={control}
            />
          </Styled.RadioButtonContainer>
          <Styled.TooltipContainer>
            <Tooltip
              id="categoryTooltip"
              text="Do you want a user to be able to select multiple within a single category?"
              icon={<BsInfoCircleFill />}
            />
          </Styled.TooltipContainer>
        </Styled.RadioButtonGroup>
      </Styled.FormContainer>
      <Styled.ButtonContainer>
        <PrimaryOutlineButton
          buttonWidth="112px"
          margin="0 24px 0 auto"
          type="button"
          icon={<BsX />}
          loading={isLoading}
          title="Cancel"
          onClick={closeCreateEditModal}
        />
        <PrimaryButton
          buttonWidth="108px"
          type="submit"
          loading={isLoading}
          title={modalAction.id ? 'Save' : 'Create'}
          onClick={isRaw && onSubmit}
        />
      </Styled.ButtonContainer>
    </Styled.Main>
  );
};

CategoryAddEditFormRaw.propTypes = propTypes;

CategoryAddEditFormRaw.defaultProps = defaultProps;

/**
 * Page that shows the form to add or edit a Category.
 * @param {object} props The props object.
 * @returns {JSX.Element} The Category Add Edit Form component.
 */
const CategoryAddEditForm = (props) => {
  const methods = useFormContext();
  return <CategoryAddEditFormRaw {...props} methods={methods} />;
};

CategoryAddEditForm.propTypes = propTypes;

CategoryAddEditForm.defaultProps = defaultProps;

/**
 * Page that shows the form to add or edit a Category.
 * @param {object} props The props object.
 * @returns {JSX.Element} The Category Add Edit Form component.
 */
export const CategoryAddEditSubForm = (props) => {
  const methods = useForm();
  const { onSubmit } = props;
  const handleSubmit = methods.handleSubmit(onSubmit);
  return (
    <CategoryAddEditFormRaw
      {...props}
      isRaw
      methods={methods}
      onSubmit={handleSubmit}
    />
  );
};

CategoryAddEditSubForm.propTypes = propTypes;

CategoryAddEditSubForm.defaultProps = defaultProps;

export default CategoryAddEditForm;
